import { queryOptions, useQuery } from '@tanstack/react-query'

import { inviteKeys } from './query-key-factory'

import { QueryConfig } from '@/lib/react-query'
import { supabase } from '@/lib/supabase-client'
import { logger } from '@/logger'

export type UserInvitation = {
  id: string
  inviteCode: string
  status: 'pending' | 'accepted' | 'expired' | 'cancelled'
  inviterRole: 'practician' | 'technician'
  createdAt: string
  expiresAt: string
  acceptedAt: string | null
  inviteeName: string | null
}

export const getUserInvitations = async (): Promise<UserInvitation[]> => {
  const { data, error } = await supabase.rpc('get_user_invitations')

  if (error) {
    logger.error('Failed to get user invitations', { errorMessage: error })
    throw error
  }

  if (!data || !Array.isArray(data)) {
    return []
  }

  return data.map(
    (invitation: {
      id: string
      invite_code: string
      status: 'pending' | 'accepted' | 'expired' | 'cancelled'
      inviter_role: 'practician' | 'technician'
      created_at: string
      expires_at: string
      accepted_at: string | null
      invitee_name: string | null
    }) => ({
      id: invitation.id,
      inviteCode: invitation.invite_code,
      status: invitation.status,
      inviterRole: invitation.inviter_role,
      createdAt: invitation.created_at,
      expiresAt: invitation.expires_at,
      acceptedAt: invitation.accepted_at,
      inviteeName: invitation.invitee_name,
    }),
  )
}

const getUserInvitationsQueryOptions = () => {
  return queryOptions({
    queryKey: inviteKeys.userInvitations(),
    queryFn: getUserInvitations,
  })
}

type UseGetUserInvitationsOptions = {
  queryConfig?: QueryConfig<typeof getUserInvitationsQueryOptions>
}

export const useGetUserInvitations = ({ queryConfig }: UseGetUserInvitationsOptions = {}) => {
  return useQuery({
    ...getUserInvitationsQueryOptions(),
    ...queryConfig,
  })
}

const getUserPendingInvitationsQueryOptions = () => {
  return queryOptions({
    queryKey: inviteKeys.userPendingInvitations(),
    queryFn: getUserInvitations,
    select: (data) => data.filter(invitation => invitation.status === 'pending'),
  })
}

type UseUserPendingInvitationsOptions = {
  queryConfig?: QueryConfig<typeof getUserPendingInvitationsQueryOptions>
}

export const useUserPendingInvitations = ({ queryConfig }: UseUserPendingInvitationsOptions = {}) => {
  return useQuery({
    ...getUserPendingInvitationsQueryOptions(),
    ...queryConfig,
  })
}
